import { clampChroma, converter, differenceCiede2000, formatHex, type Oklch } from 'culori';

const toOklch = converter('oklch');
const difference = differenceCiede2000();

/** Serialize a perceptual color while reducing chroma only to fit the display gamut. */
export function displayColor(color: Oklch): string {
  return formatHex(clampChroma(color, 'oklch'));
}

/** Adjust lightness without rotating the hue or imposing a palette's saturation. */
export function atLightness(color: string, lightness: number): string {
  return displayColor({ ...toOklch(color)!, l: lightness });
}

/** Find the smallest CIEDE2000 change that satisfies a control's actual constraints. */
export function closestColor(color: string, accepts: (candidate: string) => boolean): string | null {
  if (accepts(color)) return color;
  let best: string | null = null;
  let distance = Infinity;
  for (let step = 0; step <= 100; step++) {
    const candidate = atLightness(color, step / 100);
    if (!accepts(candidate)) continue;
    const delta = difference(color, candidate);
    if (delta < distance) { best = candidate; distance = delta; }
  }
  return best;
}

/** Derive a split-complement from the actual accent, without a prescribed palette. */
export function createBrandPalette(value?: string | null) {
  if (typeof value !== 'string') return null;
  const hex = value.trim().toLowerCase();
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(hex)) return null;
  const accent = hex.length === 4 ? `#${[...hex.slice(1)].map(channel => channel + channel).join('')}` : hex;
  const source = toOklch(accent)!;
  // A 150-degree rotation is one arm of the split-complementary relationship.
  // Scaling chroma with lightness keeps the companion's saturation proportional.
  const companion = displayColor({ ...source, l: 0.3, c: source.l ? source.c * 0.3 / source.l : 0,
    h: source.h === undefined ? undefined : (source.h + 150) % 360 });
  const ink = displayColor({ ...source, l: 0.18, c: source.l ? source.c * 0.18 / source.l : 0 });
  return { accent, companion, ink };
}
